/**
 * Storage API Client
 *
 * API client for file uploads (Google Cloud Storage)
 * Base URL: https://linkeddog-api.up.railway.app
 *
 * Flow:
 * 1. Get signed URL from backend
 * 2. Upload file directly to GCS using signed URL
 * 3. Return file URL
 */

import axios from 'axios';
import { apiClient } from './client';
import type { UploadResponse } from '@/shared/types/api.types';

/**
 * Separate axios instance for GCS uploads
 * CRITICAL: This instance has NO interceptors from apiClient
 * We don't want auth errors on GCS to trigger login redirects
 */
const gcsClient = axios.create({
  timeout: 60000, // 60 seconds for file uploads
  headers: {
    'Accept': '*/*',
  },
});

export const storageApi = {
  /**
   * Upload a file to Google Cloud Storage using signed URL
   * @param file - File to upload
   * @param folder - Folder path (e.g., 'dogs/avatars', 'dogs/poops')
   */
  upload: async (file: File, folder: string = 'dogs'): Promise<UploadResponse> => {
    try {
      // 1. Generate unique key for the file
      const fileExtension = file.name.split('.').pop();
      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 15);
      const key = `${folder}/${timestamp}-${randomString}.${fileExtension}`;

      // 2. Get signed URL from backend (uses apiClient with auth)
      const signedUrlResponse = await apiClient.post<{ url: string; key: string }>(
        '/storage/get-signed-url',
        {
          key,
          contentType: file.type,
          contentLength: file.size,
        }
      );

      const { url: signedUrl } = signedUrlResponse.data;

      // 3. Upload file directly to Google Cloud Storage
      // CRITICAL: Use separate gcsClient to avoid auth interceptors
      await gcsClient.put(signedUrl, file, {
        headers: {
          'Content-Type': file.type,
          'Content-Length': file.size.toString(),
        },
      });

      // 4. Return public URL (remove query params from signed URL)
      const publicUrl = signedUrl.split('?')[0] || '';

      return {
        url: publicUrl,
        publicUrl: publicUrl,
      };
    } catch (error) {
      // Enhanced error handling for debugging
      if (axios.isAxiosError(error)) {
        console.error('[Storage API] Upload failed:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          message: error.message,
        });

        // Distinguish between backend API errors and GCS errors
        if (error.config?.url?.includes('storage.googleapis.com')) {
          throw new Error(`Failed to upload to Google Cloud Storage: ${error.message}`);
        } else {
          throw new Error(`Failed to get signed URL from backend: ${error.message}`);
        }
      }
      throw error;
    }
  },

  /**
   * Upload an image using the new storage endpoint
   * @param file - Image file to upload
   * @param type - Type of entity ('USER' | 'DOG')
   */
  uploadImage: async (file: File, type: 'USER' | 'DOG'): Promise<import('@/shared/types/api.types').StorageImageResponse> => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);

    const response = await apiClient.post<import('@/shared/types/api.types').StorageImageResponse>(
      '/storage/image',
      formData,
      // {
      //   headers: {
      //     'Content-Type': 'multipart/form-data',
      //   },
      // }
    );

    return response.data;
  },
};
